import React, { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { useTheme } from '../../contexts/ThemeContext';
import {
  extractPayableInvoice,
  type ExtractedPayableInvoice,
} from '../../services/mutations';
import toast from 'react-hot-toast';

const ACCEPTED = '.pdf,.png,.jpg,.jpeg,.webp,.gif,.tif,.tiff';

function isAcceptedFile(file: File): boolean {
  const name = file.name.toLowerCase();
  const type = (file.type || '').toLowerCase();
  return (
    type.includes('pdf') ||
    type.startsWith('image/') ||
    /\.(pdf|png|jpe?g|webp|gif|tiff?)$/i.test(name)
  );
}

export const InvoiceIntakeDropZone: React.FC = () => {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [extracting, setExtracting] = useState(false);

  const processFile = useCallback(
    async (file: File) => {
      if (!isAcceptedFile(file)) {
        toast.error('Please upload a PDF or image invoice');
        return;
      }
      setExtracting(true);
      const toastId = toast.loading('Reading invoice…');
      try {
        const extracted: ExtractedPayableInvoice = await extractPayableInvoice(file);
        toast.dismiss(toastId);

        if (extracted.warnings?.length) {
          extracted.warnings.slice(0, 2).forEach((w) => toast(w, { icon: '⚠️', duration: 5000 }));
        } else {
          toast.success(
            extracted.extractionMethod === 'ai'
              ? `Fields filled${extracted.modelName ? ` (${extracted.modelName})` : ''}. Review and save.`
              : 'Basic fields detected. Review and complete the form.',
          );
        }

        const formPrefill: Record<string, unknown> = {
          status: 'pending',
          payeeId: extracted.payeeId != null ? String(extracted.payeeId) : '',
          payeeName: extracted.payeeName || '',
          invoiceDate: extracted.invoiceDate || undefined,
          dueDate: extracted.dueDate || undefined,
          amount: extracted.amount ?? undefined,
          notes: extracted.notes || '',
        };

        navigate('/payables/new', {
          state: {
            formPrefill,
            initialFiles: [file],
            invoiceExtractMeta: {
              confidence: extracted.confidence,
              warnings: extracted.warnings,
              possibleDuplicates: extracted.possibleDuplicates,
              extractionMethod: extracted.extractionMethod,
              invoiceNumber: extracted.invoiceNumber,
            },
          },
        });
      } catch (err: unknown) {
        toast.dismiss(toastId);
        const msg =
          (err as { response?: { data?: { message?: string | string[] } } })?.response?.data
            ?.message;
        const text = Array.isArray(msg) ? msg.join(', ') : msg;
        toast.error(text || 'Failed to read invoice');
        console.error(err);
      } finally {
        setExtracting(false);
        if (inputRef.current) inputRef.current.value = '';
      }
    },
    [navigate],
  );

  const onFiles = useCallback(
    (list: FileList | null) => {
      const file = list?.[0];
      if (file) void processFile(file);
    },
    [processFile],
  );

  return (
    <div
      className={cn(
        'relative rounded-lg border-2 border-dashed px-4 py-5 transition-colors',
        dragActive ? 'border-indigo-500 bg-indigo-50/60 dark:bg-indigo-950/30' : cn(colors.border, colors.surface),
        extracting && 'opacity-70 pointer-events-none',
      )}
      onDragEnter={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(true);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(true);
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        onFiles(e.dataTransfer.files);
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED}
        className="hidden"
        disabled={extracting}
        onChange={(e) => onFiles(e.target.files)}
      />
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
        <div
          className={cn(
            'flex h-11 w-11 shrink-0 items-center justify-center rounded-lg',
            'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300',
          )}
          aria-hidden
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.75}
              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
            />
          </svg>
        </div>
        <div className="min-w-0 flex-1">
          <p className={cn('text-sm font-medium', colors.text)}>
            {extracting ? 'Reading invoice…' : 'Drop an invoice to create a payable'}
          </p>
          <p className={cn('text-xs mt-0.5', colors.textMuted)}>
            PDF or image — we fill payee, amount, dates, and attach the file. You review before saving.
          </p>
        </div>
        <button
          type="button"
          disabled={extracting}
          onClick={() => inputRef.current?.click()}
          className={cn(
            'shrink-0 px-3 py-2 text-sm font-medium rounded-md',
            'bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60',
          )}
        >
          {extracting ? 'Working…' : 'Choose file'}
        </button>
      </div>
    </div>
  );
};

export default InvoiceIntakeDropZone;
